// Admin RBAC user management — with module-level permissions, password reset,
// edit user ID, enable/disable, delete.
import React, { useEffect, useState } from 'react';
import { Plus, KeyRound, Trash2, X, Pencil, Save } from 'lucide-react';
import { api } from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const ROLES = ['super_admin', 'admin', 'operations', 'finance', 'support'];

const PermissionToggles = ({ modules, value, onChange, disabled }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-72 overflow-auto p-2 border border-gray-200 dark:border-gray-700 rounded-lg">
    {modules.map((m) => {
      const on = value.includes(m);
      return (
        <label key={m} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded cursor-pointer ${
          on ? 'bg-red-50 dark:bg-red-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <input
            type="checkbox"
            checked={on}
            disabled={disabled}
            onChange={(e) => {
              if (disabled) return;
              const next = e.target.checked ? [...value, m] : value.filter((x) => x !== m);
              onChange(next);
            }}
            data-testid={`perm-toggle-${m}`}
          />
          <span className="capitalize text-gray-700 dark:text-gray-200">{m.replace(/_/g, ' ')}</span>
        </label>
      );
    })}
  </div>
);

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [modules, setModules] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', full_name: '', admin_role: 'support', mobile: '', permissions: [], user_id: '' });
  const [editForm, setEditForm] = useState({ permissions: [], admin_role: 'support', full_name: '', mobile: '', new_user_id: '' });
  const [resetPwd, setResetPwd] = useState('');

  const reload = () => {
    Promise.all([
      api.get('/admin/users'),
      api.get('/admin/audit-logs'),
      api.get('/admin/users/permissions/modules'),
    ])
      .then(([u, a, m]) => {
        setUsers(u.data.users || []);
        setAudit(a.data.logs || []);
        setModules(m.data.modules || []);
        setDefaults(m.data.defaults || {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(reload, []);

  const onRoleChange = (role) => {
    setForm((f) => ({ ...f, admin_role: role, permissions: defaults[role] || [] }));
  };

  const create = async () => {
    if (!form.email || !form.password || !form.full_name) { toast.error('Email, password and name are required.'); return; }
    try {
      const body = { ...form };
      if (!body.user_id) delete body.user_id;
      await api.post('/admin/users', body);
      toast.success('Admin user created.');
      setCreating(false);
      setForm({ email: '', password: '', full_name: '', admin_role: 'support', mobile: '', permissions: [], user_id: '' });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create user');
    }
  };

  const beginEdit = (u) => {
    setEditingId(u.id);
    setEditForm({
      permissions: u.permissions || [],
      admin_role: u.admin_role || 'support',
      full_name: u.full_name || '',
      mobile: u.mobile || '',
      new_user_id: u.id,
    });
  };

  const saveEdit = async (u) => {
    try {
      const body = { ...editForm };
      if (body.new_user_id === u.id) delete body.new_user_id;
      await api.put(`/admin/users/${u.id}`, body);
      toast.success('User updated.');
      setEditingId(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { active: !u.active });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success('User deleted.');
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  const resetPassword = async () => {
    if (!resetTarget || !resetPwd || resetPwd.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: resetPwd });
      toast.success('Password reset. User will be logged out.');
      setResetTarget(null);
      setResetPwd('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Users & Permissions</h1>
        <Button onClick={() => setCreating(true)} className="bg-red-600 hover:bg-red-700" data-testid="admin-users-create-btn">
          <Plus className="h-4 w-4 mr-1" /> New Admin
        </Button>
      </div>

      {/* Users list */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
              <tr>
                <th className="text-left px-4 py-2 font-medium">User ID</th>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Email</th>
                <th className="text-left px-4 py-2 font-medium">Mobile</th>
                <th className="text-left px-4 py-2 font-medium">Role</th>
                <th className="text-left px-4 py-2 font-medium">Permissions</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isEditing = editingId === u.id;
                return (
                  <tr key={u.id} className="border-t border-gray-100 dark:border-gray-800" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-4 py-2 font-mono text-[11px]">
                      {isEditing ? (
                        <Input value={editForm.new_user_id} onChange={(e) => setEditForm({ ...editForm, new_user_id: e.target.value })} className="h-7 w-28 font-mono" />
                      ) : u.id}
                    </td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <Input value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} className="h-7" />
                      ) : (u.full_name || '—')}
                    </td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400">{u.email}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-400 text-xs">
                      {isEditing ? (
                        <Input value={editForm.mobile} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} className="h-7 w-32" />
                      ) : (u.mobile || '—')}
                    </td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <select value={editForm.admin_role} onChange={(e) => setEditForm({ ...editForm, admin_role: e.target.value, permissions: defaults[e.target.value] || [] })}
                                className="text-xs h-7 border rounded px-1 dark:bg-gray-800">
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                      ) : (
                        <Badge variant="outline" className="capitalize">{(u.admin_role || '—').replace(/_/g, ' ')}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-[10px] text-gray-500" data-testid={`admin-user-perm-count-${u.id}`}>
                        {u.admin_role === 'super_admin' ? `All (${modules.length})` : `${(u.permissions || []).length} modules`}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <button onClick={() => toggleActive(u)} className={`text-[11px] font-semibold ${u.active ? 'text-emerald-600' : 'text-rose-600'}`}
                              data-testid={`admin-user-status-${u.id}`}>
                        {u.active ? 'Active' : 'Disabled'}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-right space-x-1">
                      {isEditing ? (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => saveEdit(u)} data-testid={`admin-user-save-${u.id}`}>
                            <Save className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => beginEdit(u)} title="Edit"
                                  data-testid={`admin-user-edit-${u.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setResetTarget(u)} title="Reset password"
                                  data-testid={`admin-user-reset-${u.id}`}>
                            <KeyRound className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteUser(u)} title="Delete"
                                  data-testid={`admin-user-delete-${u.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && !loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400">No users yet.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Per-row permissions panel (only shown when editing) */}
      {editingId && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold">Module permissions</p>
              <p className="text-xs text-gray-500">{editForm.admin_role === 'super_admin' ? 'Super admin has access to all modules implicitly.' : `${editForm.permissions.length} of ${modules.length} enabled`}</p>
            </div>
            <PermissionToggles modules={modules} value={editForm.permissions} disabled={editForm.admin_role === 'super_admin'}
                                onChange={(perms) => setEditForm({ ...editForm, permissions: perms })} />
          </CardContent>
        </Card>
      )}

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-create-user-modal">
          <Card className="max-w-2xl w-full">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold">New Admin User</h3>
                <Button variant="ghost" size="sm" onClick={() => setCreating(false)}><X className="h-4 w-4" /></Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Full name *" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                <Input placeholder="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <Input placeholder="Mobile (optional)" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
                <Input placeholder="Custom User ID (optional)" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} />
                <Input placeholder="Password *" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                <select value={form.admin_role} onChange={(e) => onRoleChange(e.target.value)}
                        className="px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-md dark:bg-gray-800">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs font-semibold mb-1">Module permissions ({form.admin_role === 'super_admin' ? 'all granted' : `${form.permissions.length} selected`})</p>
                <PermissionToggles modules={modules} value={form.permissions} disabled={form.admin_role === 'super_admin'}
                                    onChange={(perms) => setForm({ ...form, permissions: perms })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
                <Button onClick={create} className="bg-red-600 hover:bg-red-700" data-testid="admin-create-user-submit">Create</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="admin-reset-pwd-modal">
          <Card className="max-w-sm w-full">
            <CardContent className="p-5 space-y-3">
              <h3 className="text-lg font-bold">Reset password</h3>
              <p className="text-xs text-gray-500">New password for <b>{resetTarget.email}</b>. All sessions will be revoked.</p>
              <Input type="password" placeholder="New password (min 8 chars)" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setResetTarget(null); setResetPwd(''); }}>Cancel</Button>
                <Button onClick={resetPassword} className="bg-red-600 hover:bg-red-700" data-testid="admin-reset-pwd-submit">Reset</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Audit log */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-2">Audit log</h3>
          {audit.length === 0 ? (
            <p className="text-xs text-gray-400">No audit events yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-gray-400">
                <tr>
                  <th className="text-left pb-1">When</th>
                  <th className="text-left pb-1">Actor</th>
                  <th className="text-left pb-1">Action</th>
                  <th className="text-left pb-1">Target</th>
                </tr>
              </thead>
              <tbody>
                {audit.slice(0, 50).map((a, i) => (
                  <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-1">{a.at ? new Date(a.at).toLocaleString() : a.timestamp ? new Date(a.timestamp).toLocaleString() : '—'}</td>
                    <td className="py-1 font-mono text-[10px]">{a.actor_email || a.actor_id || '—'}</td>
                    <td className="py-1 capitalize">{(a.action || '—').replace(/_/g, ' ')}</td>
                    <td className="py-1 font-mono text-[10px]">{a.target_id || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminUsers;
